import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";

export const simpleRichText = tv({
  base: cn(
    "uipm:font-[var(--font-family-sans)]",
    "uipm:font-medium",
    "uipm:[&_h1]:heading-1",
    "uipm:[&_h1]:text-[var(--typography-colors-text-heading)]",
    "uipm:[&_h1]:mb-[var(--container-title-gap)]",
    "uipm:[&_h2]:heading-2",
    "uipm:[&_h2]:text-[var(--typography-colors-text-heading)]",
    "uipm:[&_h2]:mb-[var(--container-title-gap)]",
    "uipm:[&_h3]:heading-3",
    "uipm:[&_h3]:text-[var(--typography-colors-text-heading)]",
    "uipm:[&_h3]:mb-[var(--container-title-gap)]",
    "uipm:[&_h4]:heading-4",
    "uipm:[&_h4]:text-[var(--typography-colors-text-heading)]",
    "uipm:[&_h4]:mb-[var(--container-title-gap)]",
    "uipm:[&_h5]:heading-5",
    "uipm:[&_h5]:text-[var(--typography-colors-text-heading)]",
    "uipm:[&_h5]:mb-[var(--container-title-gap)]",
    "uipm:[&_h6]:heading-6",
    "uipm:[&_h6]:text-[var(--typography-colors-text-heading)]",
    "uipm:[&_h6]:mb-[var(--container-title-gap)]",
    // Paragraph styling - spacing between paragraphs should equal their line-height
    "uipm:[&_p]:mb-[var(--typography-line-heights-body-medium)]",
    "uipm:[&_p]:last:mb-0",
    // Link styling
    "uipm:[&_a]:text-[var(--typography-colors-link)]",
    "uipm:[&_a]:font-medium",
    "uipm:[&_a]:underline",
    "uipm:[&_a]:decoration-solid",
    "uipm:[&_a]:decoration-auto",
    "uipm:[&_a]:underline-offset-auto",
    // List styling - unordered lists
    "uipm:[&_ul]:list-disc",
    "uipm:[&_ul]:list-outside",
    "uipm:[&_ul]:pl-6",
    "uipm:[&_ul]:mb-4",
    // List styling - ordered lists
    "uipm:[&_ol]:list-decimal",
    "uipm:[&_ol]:list-outside",
    "uipm:[&_ol]:pl-6",
    "uipm:[&_ol]:mb-4",
    // List items
    "uipm:[&_li]:mb-2",
    // Table container for horizontal scroll - direct table styling
    "[&_table]:uipm:w-full [&_table]:uipm:border-collapse [&_table]:uipm:my-8",
    "[&_table]:uipm:rounded-[var(--radius-radius-sm)] [&_table]:uipm:overflow-hidden [&_table]:uipm:shadow-sm",
    "[&_table]:uipm:block [&_table]:uipm:overflow-x-auto",
    // Table header styling
    "[&_thead]:uipm:bg-[var(--surface-surface-accent)]",
    "[&_thead_tr]:uipm:border-b [&_thead_tr]:uipm:border-[var(--color-grey-300)]",
    "[&_th]:uipm:px-6 [&_th]:uipm:py-4",
    "[&_th]:uipm:text-left [&_th]:uipm:font-bold",
    "[&_th]:uipm:fz-[var(--typography-sizes-body-large,18px)]",
    "[&_th]:uipm:leading-[var(--typography-line-heights-body-large,28px)]",
    "[&_th]:uipm:min-w-[200px] [&_th]:uipm:whitespace-normal",
    "[&_th]:uipm:align-top",
    // Note: th text color is set globally in utilities.css with !important
    // Table body styling
    "[&_tbody]:uipm:bg-[var(--surface-surface-primary,#FFFFFF)]",
    "[&_tbody_tr]:uipm:border-b [&_tbody_tr]:uipm:border-[var(--color-grey-300,#E0E0E0)]",
    "[&_tbody_tr:last-child]:uipm:border-b-0",
    "[&_tbody_tr:nth-child(even)]:uipm:bg-[var(--color-grey-50,#F5F5F5)]",
    "[&_tbody_tr:hover]:uipm:bg-[var(--surface-surface-accent-light,#E8F5E9)]",
    // Table cell styling
    "[&_td]:uipm:px-6 [&_td]:uipm:py-4",
    "[&_td]:uipm:fz-[var(--typography-sizes-body-large,18px)]",
    "[&_td]:uipm:leading-[var(--typography-line-heights-body-large,28px)]",
    "[&_td]:uipm:text-[var(--typography-colors-text-faded-dark,#545050)]",
    "[&_td]:uipm:min-w-[200px] [&_td]:uipm:whitespace-normal",
    "[&_td]:uipm:align-top",
  ),
  variants: {
    size: {
      small: cn(
        "uipm:fz-[var(--typography-sizes-body-small)] uipm:leading-[var(--typography-line-heights-body-xs)]",
      ),
      medium: cn(
        "uipm:fz-[var(--typography-sizes-body-medium)] uipm:leading-[var(--typography-line-heights-body-medium)]",
      ),
      large: cn(
        "uipm:fz-[var(--typography-sizes-body-large)] uipm:leading-[var(--typography-line-heights-body-large)]",
      ),
    },
    fontColor: {
      default: "uipm:text-[var(--typography-colors-text-body)]",
      accent: "uipm:text-[var(--typography-colors-text-accent)]",
      faded: "uipm:text-[var(--typography-colors-text-faded)]",
      "faded-dark": "uipm:text-[var(--typography-colors-text-faded-dark)]",
      inverted: "uipm:text-[var(--typography-colors-text-inverted)]",
    },
  },
  defaultVariants: {
    size: "medium",
    fontColor: "default",
  },
});

export type SimpleRichTextProps = VariantProps<typeof simpleRichText> & {
  size?: "small" | "medium" | "large";
  fontColor?: "default" | "accent" | "faded" | "faded-dark" | "inverted";
  className?: string;
} & ComponentProps<"div">;

export const SimpleRichText = React.forwardRef<
  HTMLDivElement,
  SimpleRichTextProps
>(({ size = "medium", fontColor = "default", className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(simpleRichText({ size, fontColor }), className)}
      {...props}
    />
  );
});

SimpleRichText.displayName = "SimpleRichText";
